import React, { useRef } from "react";
import dayjs from "dayjs";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import {
  useGetMicroServiceMonitoringGraphDataQuery,
  useGetMicroServiceMonitoringForcastQuery,
} from "./operatorDashboardAPISlice";
import GraphDownloadButton from "../../Global/other/GraphDownloadButton";
import { axisTimeLabel, spansDays } from "./EChartsOperatorGraph";
import { formatSAST } from "../../Global/other/formatSAST";

const labelColor = "rgba(255,255,255,0.45)";
const gridLineColor = "rgba(255,255,255,0.05)";
const geyserColor = "#FDB940";

function makeGradient(color) {
  return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: color + "55" },
    { offset: 1, color: color + "05" },
  ]);
}

export default function EChartsGeyserGraph({ pollingInterval = 0, request }) {
  const chartRef = useRef(null);
  const {
    data: microServiceGraphData,
    isLoading,
    isError,
  } = useGetMicroServiceMonitoringGraphDataQuery(request, { pollingInterval });

  const {
    data: microserviceForcastState,
    isLoading: l3,
    isError: e3,
  } = useGetMicroServiceMonitoringForcastQuery(null, { pollingInterval });

  if (isLoading) {
    return (
      <Grid
        item
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
        }}
      >
        <CircularProgress size={20} sx={{ color: geyserColor }} />
        <Typography sx={{ color: labelColor }}>Loading geyser data…</Typography>
      </Grid>
    );
  }

  if (isError) {
    return (
      <Grid item sx={{ width: "100%", p: 2 }}>
        <Typography sx={{ color: "#e05050" }}>
          Error loading geyser data
        </Typography>
      </Grid>
    );
  }

  const data = microServiceGraphData || [];
  const demandData = microServiceGraphData || [];
  const forecastData = microserviceForcastState || [];

  const lastDemandTime = demandData.length
    ? new Date(demandData[demandData.length - 1].interval).getTime()
    : 0;

  const lastForecastTime = forecastData.length
    ? new Date(
        forecastData[forecastData.length - 1].forecast_timestamp,
      ).getTime()
    : 0;

  // const xAxisMax = Math.max(lastDemandTime, lastForecastTime);

  const option = {
    backgroundColor: "transparent",
    animation: false,
    grid: { top: 16, right: 16, bottom: 72, left: 56 },
    dataZoom: [
      { type: "inside", xAxisIndex: 0, filterMode: "filter" },
      {
        type: "slider",
        xAxisIndex: 0,
        height: 18,
        bottom: 32,
        backgroundColor: "rgba(255,255,255,0.03)",
        borderColor: "rgba(255,255,255,0.07)",
        fillerColor: geyserColor + "33",
        handleStyle: { color: geyserColor, borderColor: geyserColor },
        moveHandleStyle: { color: geyserColor },
        emphasis: { handleStyle: { color: geyserColor } },
        textStyle: { color: "transparent" },
        brushSelect: false,
        showDetail: false,
      },
    ],
    toolbox: {
      right: 8,
      top: 4,
      feature: {
        dataZoom: {
          yAxisIndex: "none",
          title: { zoom: "Zoom", back: "Reset" },
          iconStyle: { borderColor: labelColor },
          emphasis: { iconStyle: { borderColor: geyserColor } },
        },
        restore: {
          title: "Reset",
          iconStyle: { borderColor: labelColor },
          emphasis: { iconStyle: { borderColor: geyserColor } },
        },
      },
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: "#1C232C",
      borderColor: "rgba(255,255,255,0.12)",
      textStyle: { color: "#e0e6f0", fontSize: 12 },
      axisPointer: {
        type: "line",
        lineStyle: { color: "rgba(255,255,255,0.25)", type: "dashed" },
      },
      // formatter: (params) => {
      //   if (!params.length) return "";
      //   const d = new Date(params[0].value[0]);
      //   const today = new Date();
      //   const isToday =
      //     d.getFullYear() === today.getFullYear() &&
      //     d.getMonth() === today.getMonth() &&
      //     d.getDate() === today.getDate();
      //   const dateStr = isToday
      //     ? `Today · ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
      //     : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} · ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      //   const rows = params
      //     .filter((p) => p.value?.[1] != null)
      //     .map(
      //       (p) =>
      //         `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:6px"></span>` +
      //         `${p.seriesName}&nbsp;&nbsp;<b>${Number(p.value[1]).toFixed(3)} MVA</b>`,
      //     )
      //     .join("<br/>");
      //   return `<div style="font-size:11px;color:rgba(255,255,255,0.45);margin-bottom:6px">${dateStr}</div>${rows}`;
      // },
    },
    legend: {
      bottom: 4,
      textStyle: { color: labelColor, fontSize: 11 },
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 18,
      data: [{ name: "Tjommie demand (kW)", icon: "circle" }],
    },

    xAxis: {
      type: "category",
      data: data?.map((item) => item.interval),
      axisLine: { lineStyle: { color: gridLineColor } },
      axisTick: { show: false },
      axisLabel: {
        color: labelColor,
        fontSize: 10,
        // Dated on multi-day ranges, like the Demand chart above it, so the two axes read
        // the same (NEU-1057).
        formatter: (v) => axisTimeLabel(v, spansDays(data)),
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: labelColor,
        fontSize: 10,
        formatter: (v) => v.toFixed(2),
      },
      splitLine: { lineStyle: { color: gridLineColor, type: "dashed" } },
    },
    series: [
      {
        name: "Tjommie demand (kW)",
        type: "line",
        smooth: true,
        symbol: "none",
        lineStyle: { color: geyserColor, width: 2 },
        areaStyle: { color: makeGradient(geyserColor) },
        itemStyle: { color: geyserColor },

        data: data.map((item) =>
          item.geyser_demand != null ? item.geyser_demand / 1000000 : null,
        ),
      },
    ],
  };

  return (
    <Grid item sx={{ width: "100%" }}>
      <Box
        sx={{
          background: "#161B22",
          border: "1px solid rgba(255,255,255,0.06)",
          borderRadius: "12px",
          overflow: "hidden",
          padding: "20px 20px 16px",
        }}
      >
        {/* Card header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "16px" }}>
          <Box>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#fff" }}>
              Tjommie demand
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)", mt: "2px" }}>
              Sum of geyser load (kW) · separate aligned plot used when the dual-axis view isn't legible — shares the demand chart's time axis
            </Typography>
          </Box>
          <GraphDownloadButton
            onDownloadCsv={() => {
              const rows = microServiceGraphData || [];
              if (!rows.length) return;
              let csv = "Timestamp (SAST),Tjommie Demand (kW)\r\n";
              rows.forEach((item) => {
                const dt = formatSAST(item.interval);
                const val = item.geyser_demand != null ? (item.geyser_demand / 1000000).toFixed(4) : "";
                csv += `${dt},${val}\r\n`;
              });
              const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `Geyser_Demand_${dayjs().format("YYYY-MM-DD")}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            onDownloadPng={() => {
              const url = chartRef.current?.getEchartsInstance()?.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: "#161B22" });
              if (!url) return;
              const a = document.createElement("a");
              a.href = url;
              a.download = `Geyser_Demand_${dayjs().format("YYYY-MM-DD")}.png`;
              a.click();
            }}
          />
        </Box>

        <ReactECharts ref={chartRef} option={option} style={{ height: 320 }} notMerge />
      </Box>
    </Grid>
  );
}
